import { usePopover } from '@/hooks/usePopover';
import { useToast } from '@/hooks/useToast';
import { ReportPageObjectModel } from '@/poms/reportPageObjectModel';
import { getBaseUrl } from '@/services/api';
import { getAuthToken } from '@/services/auth/getAuthToken';
import { useTenants } from '@/services/tenants/useTenants';
import { DownloadFormat, downloadFromSource } from '@/utils/downloadUtils';
import { FileDownloadOutlined, InsertDriveFileOutlined } from '@mui/icons-material';
import { Divider, IconButton, ListItemIcon, Tooltip, useTheme } from '@mui/material';
import { useCallback } from 'react';
import { StyledList, StyledListHeader, StyledListItem } from '../AssetsPage/PopoverList';
import { BodyText } from '../BodyText';
import { RoundedPopover } from '../Popover/RoundedPopover';

export const ReportDownload = ({ id }: { id: string }) => {
  const { getTenantId } = useTenants();
  const tenantId = getTenantId();
  const { showToast } = useToast();
  const theme = useTheme();

  const {
    open: openDownload,
    close: closeDownload,
    isOpen: isDownloadOpen,
    anchorElement: downloadAnchorElement,
  } = usePopover();

  const handleDownload = useCallback(
    async (format: DownloadFormat) => {
      if (!id || !tenantId) return;

      try {
        const authToken = await getAuthToken();
        const response = await fetch(`${getBaseUrl()}/tenant/${tenantId}/reports/${id}/file?format=${format}`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        });

        const filename = `report-${id}.${format}`;
        await downloadFromSource(response, { filename });
      } catch {
        showToast({ message: `Failed to download ${format.toUpperCase()}`, type: 'error' });
      } finally {
        closeDownload();
      }
    },
    [id, tenantId, showToast, closeDownload]
  );

  const formats: DownloadFormat[] = ['csv', 'xlsx'];
  const formatLabels: Record<DownloadFormat, string> = {
    csv: 'CSV',
    xlsx: 'XLSX',
    xls: 'XLS',
  };

  return (
    <>
      <Tooltip title='Download Report' placement='bottom'>
        <IconButton
          size='small'
          onClick={openDownload}
          data-testid={ReportPageObjectModel.ReportDownloadButton}
          sx={{
            color: theme.palette.text.primary,
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          <FileDownloadOutlined fontSize='small' />
        </IconButton>
      </Tooltip>
      <RoundedPopover
        open={isDownloadOpen}
        anchorEl={downloadAnchorElement}
        onClose={closeDownload}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        slotProps={{
          paper: {
            sx: {
              marginTop: '4px',
              borderRadius: '12px',
            },
          },
        }}
      >
        <StyledList>
          <StyledListHeader>
            <BodyText>Download Report</BodyText>
          </StyledListHeader>
          <Divider />
          {formats.map(format => (
            <StyledListItem
              key={format}
              onClick={() => void handleDownload(format)}
              sx={{ cursor: 'pointer' }}
              data-testid={`${ReportPageObjectModel.ReportDownloadButton}-${format}-option`}
            >
              <ListItemIcon>
                <InsertDriveFileOutlined color='action' sx={{ fontSize: '18px' }} />
              </ListItemIcon>
              <BodyText>{formatLabels[format]}</BodyText>
            </StyledListItem>
          ))}
        </StyledList>
      </RoundedPopover>
    </>
  );
};
